import React, { Component } from 'react';
import { connect } from 'react-redux';
import { withTranslation, WithTranslation } from 'react-i18next';
import './GoalIndividualPage.css';

// Import SVG icons
// import { ReactComponent as ArrowLeftIcon } from '../../../assets/icons/arrow-left.svg';

// Import components
import GoalTagComponent from '../../../components/GoalTagComponent';
import PhotoCarousel from '../../../components/PhotoCarousel';
import GalleryComponent from '../../../components/GalleryComponent';

// Import Redux actions
import GetChildrenActions from '../../../redux/GetChildrenRedux';
import AttemptGoalActions from '../../../redux/AttemptGoalRedux';
import CurrentSettingsActions from '../../../redux/CurrentSettingsRedux';
import AlertBox from '../../../components/AlertBox';

interface GoalIndividualPageProps {
  currentSettings?: any;
  navigate?: (path: string, options?: any) => void;
  location?: any;
  goalId?: string | number;
  attemptGoalFetching?: boolean;
  attemptGoalSuccess?: boolean;
  children?: any[];
  attemptGoal?: (id: number) => void;
  getChildren?: () => void;
  setCurrentChild?: (child: any) => void;
  t?: (key: string) => string;
}

interface GoalIndividualPageState {
  loading: boolean;
  goalId: number | null;
  goal: any;
  childName: string;
  id: number;
  coverPhoto: any[];
  videoUrl: string;
  goalTag: string;
  totalAttempts: number;
  monthlyAttempts: number;
  description: string;
  suggestedActivities: string;
  notes: string;
  albums: any[];
  props: any;
  showAlert: boolean;
}

class GoalIndividualPage extends Component<GoalIndividualPageProps, GoalIndividualPageState> {
  defaultCoverPhoto = 'default-goal-cover-photo.png';

  constructor(props: GoalIndividualPageProps) {
    super(props);
    
    // Get goal ID from props, URL parameters, or location state (like reference project)
    const goalId = props.goalId || 
                   props.location?.state?.goalId || 
                   this.extractGoalIdFromPath(props.location?.pathname) ||
                   null;
    
    // console.log('Constructor - Goal ID extracted:', goalId);
    // console.log('Constructor - Props goalId:', props.goalId);
    // console.log('Constructor - Location state:', props.location?.state);
    // console.log('Constructor - Location pathname:', props.location?.pathname);
    
    // Initialize state with default values
    this.state = {
      loading: false,
      goalId,
      goal: null,
      childName: '',
      id: goalId ? (typeof goalId === 'string' ? parseInt(goalId, 10) : goalId) : 0, // Set id immediately if goalId is available
      coverPhoto: [],
      videoUrl: '',
      goalTag: '',
      totalAttempts: 0,
      monthlyAttempts: 0,
      description: '',
      suggestedActivities: '',
      notes: '',
      albums: [],
      props: null,
      showAlert: false,
    };
  }

  // Helper method to extract goal ID from URL path
  extractGoalIdFromPath = (pathname?: string): string | null => {
    if (!pathname) return null;
    
    // Try to match /goals/individual/{goalId} pattern
    const match = pathname.match(/\/goals\/individual\/(\d+)/);
    if (match && match[1]) {
      // console.log('Extracted goal ID from path:', match[1]);
      return match[1];
    }
    
    // Fallback: get the last segment if it looks like a number
    const segments = pathname.split('/');
    const lastSegment = segments[segments.length - 1];
    if (lastSegment && /^\d+$/.test(lastSegment)) {
      // console.log('Extracted goal ID from last segment:', lastSegment);
      return lastSegment;
    }
    
    return null;
  }

  componentDidMount() {
    console.log('GoalIndividualPage mounted with goalId:', this.state.goalId);
    // console.log('GoalIndividualPage mounted with id:', this.state.id);
    // console.log('Location state:', this.props.location?.state);
    // console.log('Location pathname:', this.props.location?.pathname);
    console.log('Current settings:', this.props.currentSettings);
    console.log('Props goalId:', this.props.goalId);

    if (this.props.getChildren) {
      this.props.getChildren();
    }
    
    // Try to immediately load goal data if available (like reference project)
    if (this.state.goalId && this.props.currentSettings?.child?.goals) {
      this.loadGoalData();
    } else {
      // Add a small delay to prevent race condition with Redux state
      setTimeout(() => {
        this.loadGoalData();
      }, 100);
    }
  }

  static getDerivedStateFromProps(nextProps: GoalIndividualPageProps, prevState: GoalIndividualPageState) {
    // console.log('getDerivedStateFromProps nextProps', nextProps);
    // console.log('getDerivedStateFromProps prevState', prevState);
    // Check if we have previous props to compare
    if (prevState.props) {
      // Check if goal attempt was successful
      if (!nextProps.attemptGoalFetching && nextProps.attemptGoalSuccess !== prevState.props.attemptGoalSuccess) {
        // Show success alert (Phase 10: Advanced Features)
        if (nextProps.attemptGoalSuccess) {
          // Create a web-friendly alert (equivalent to React Native's Alert.alert)
          // const alertMessage = nextProps.t ? nextProps.t('goal-individual-screen:alert.message') : 'Your child is one step closer to achieving this goal.';
          // const alertTitle = nextProps.t ? nextProps.t('goal-individual-screen:alert.title') : 'Thanks for practising';
          prevState.showAlert = true;
          if (nextProps.getChildren) {
            nextProps.getChildren();
          }

          // Use browser's native alert for now (can be replaced with custom modal later)
          // if (window.confirm(`${alertTitle}\n\n${alertMessage}\n\nClick OK to go back to goals.`)) {
          //   // Navigate back to goals page
          //   if (nextProps.navigate && typeof nextProps.navigate === 'function') {
          //     // Use setTimeout to prevent race condition
          //     setTimeout(() => {
          //       if (typeof nextProps.navigate === 'function') {
          //         nextProps.navigate('/goals');
          //       }
          //     }, 50);
          //   }
          // }
        }

        // Refresh children data AFTER showing alert
        if (nextProps.getChildren) {
          // Use setTimeout to prevent race condition
          setTimeout(() => {
            if (typeof nextProps.getChildren === 'function') {
              nextProps.getChildren();
            }
          }, 100);
        }

        return {
          props: nextProps,
        };
      }

      // Check if children data was updated
      if (nextProps.children && nextProps.currentSettings?.child?.goals && nextProps.location?.state?.goalId) {
        // Find and set current child
        if (nextProps.setCurrentChild && nextProps.currentSettings?.child?.id) {
          const currentChild = nextProps.children.find((obj: any) => obj.id === nextProps.currentSettings?.child?.id);
          if (currentChild) {
            // Use setTimeout to prevent race condition
            setTimeout(() => {
              if (typeof nextProps.setCurrentChild === 'function') {
                nextProps.setCurrentChild(currentChild);
              }
            }, 50);
          }
        }

        // Update goal data from new children data
        const goal = nextProps.currentSettings.child.goals.find((g: any) => g.id === nextProps.location.state.goalId);
        if (goal) {
          return {
            coverPhoto: goal.cover_photo || [],
            videoUrl: goal.youtube_video_url || '',
            goalTag: goal.tag?.abbreviation || '',
            totalAttempts: goal.attempts?.total || 0,
            monthlyAttempts: goal.attempts?.this_month || 0,
            description: goal.title || '',
            suggestedActivities: goal.suggested_activities || '',
            notes: goal.parent_notes || '',
            albums: goal.images || [],
            props: nextProps,
          };
        }
      }
    }

    // Return null to indicate no change to state, or update props
    return {
      props: nextProps
    };
  }

  componentDidUpdate(prevProps: GoalIndividualPageProps) {
    // Check if goal ID changed
    const newGoalId = this.props.goalId || 
                      this.props.location?.state?.goalId || 
                      this.extractGoalIdFromPath(this.props.location?.pathname);
    
    const prevGoalId = prevProps.goalId || 
                       prevProps.location?.state?.goalId || 
                       this.extractGoalIdFromPath(prevProps.location?.pathname);
    
    if (newGoalId !== prevGoalId) {
      console.log('Goal ID changed from', this.state.goalId, 'to', newGoalId);
      this.setState({ goalId: newGoalId }, () => {
        this.loadGoalData();
      });
    }

    // Check if currentSettings data became available
    if (!prevProps.currentSettings?.child?.goals && this.props.currentSettings?.child?.goals) {
      this.loadGoalData();
    }

    // Check if children data was updated
    if (prevProps.children && this.props.children && JSON.stringify(prevProps.children) !== JSON.stringify(this.props.children) ) {
      console.log('children data was updated', this.props.children);
      const updatedChild = this.props.children.find((obj: any) => obj.id === this.props.currentSettings?.child?.id);
      if (updatedChild && this.props.setCurrentChild) {
        console.log('updatedChild', updatedChild);
        this.props.setCurrentChild(updatedChild);
        // this.loadGoalData();
      }
    }

    if (prevProps.currentSettings && this.props.currentSettings && JSON.stringify(prevProps.currentSettings) !== JSON.stringify(this.props.currentSettings) ) {
      if (this.props.currentSettings?.child && this.props.currentSettings?.child?.id) {
        // this.props.setCurrentChild(this.props.currentSettings?.child);
        this.loadGoalData();
      }
    }

    // Check if we were loading and now have data
    if (this.state.loading && this.props.currentSettings?.child?.goals) {
      this.loadGoalData();
    }
  }

  loadGoalData = () => {
    const { goalId } = this.state;
    const { currentSettings } = this.props;
    
    console.log('loadGoalData called with:', { goalId, currentSettings });
    
    if (goalId && currentSettings?.child?.goals) {
      // Convert goalId to number if it's a string, or use as is if it's already a number
      const numericGoalId = typeof goalId === 'string' ? parseInt(goalId, 10) : goalId;
      console.log('Looking for goal with ID:', numericGoalId);
      console.log('Available goals:', currentSettings.child.goals);
      
      const goal = currentSettings.child.goals.find((g: any) => g.id === numericGoalId);
      if (goal) {
        console.log('Goal found:', goal);
        this.setState({
          goal,
          childName: currentSettings.child.nickname || '',
          id: goal.id, // This is crucial for the attempt button
          coverPhoto: goal.cover_photo || [],
          videoUrl: goal.youtube_video_url || '',
          goalTag: goal.tag?.abbreviation || '',
          totalAttempts: goal.attempts?.total || 0,
          monthlyAttempts: goal.attempts?.this_month || 0,
          description: goal.title || '',
          suggestedActivities: goal.suggested_activities || '',
          notes: goal.parent_notes || '',
          albums: goal.images || [],
          loading: false, // Clear loading state
        });
        // console.log('State updated with goal ID:', goal.id);
      } else {
        // console.error('Goal not found for ID:', goalId);
        this.setState({ loading: false });
      }
    } else {
      // console.log('Data not available yet:', { goalId, hasCurrentSettings: !!currentSettings, hasChild: !!currentSettings?.child, hasGoals: !!currentSettings?.child?.goals });
      // If data is not available yet, set loading state
      this.setState({ loading: true });
    }
  }

  _onBackPress = () => {
    const { navigate } = this.props;
    this.setState({ showAlert: false });
    if (navigate) {
      navigate('/goals');
    }
  }

  _onAttemptPress = () => {
    const { attemptGoal } = this.props;
    const { id, goalId } = this.state;
    
    console.log('Attempt button clicked. State ID:', id, 'Type:', typeof id);
    // console.log('State goalId:', goalId, 'Type:', typeof goalId);
    // console.log('Full state:', this.state);
    // console.log('Props attemptGoal:', attemptGoal);
    // console.log('Props attemptGoal type:', typeof attemptGoal);
    // console.log('Props attemptGoal function:', attemptGoal?.toString());
    
    // Try to get ID from multiple sources
    let finalId: number | null = id;
    if (!finalId || finalId === 0) {
      finalId = goalId ? (typeof goalId === 'string' ? parseInt(goalId, 10) : goalId) : null;
    }
    if (!finalId || finalId === 0) {
      finalId = this.props.goalId ? (typeof this.props.goalId === 'string' ? parseInt(this.props.goalId, 10) : this.props.goalId) : null;
    }
    
    console.log('Final ID to use:', finalId, 'Type:', typeof finalId);
    
    if (!finalId || finalId === 0) {
      console.error('Invalid goal ID for attempt:', finalId);
      alert('Error: Goal ID not found. Please refresh the page.');
      return;
    }
    
    if (attemptGoal && typeof attemptGoal === 'function') {
      console.log('Calling attemptGoal with ID:', finalId);
      attemptGoal(finalId);
    } else {
      console.error('attemptGoal function not available');
      console.error('attemptGoal value:', attemptGoal);
      console.error('attemptGoal type:', typeof attemptGoal);
    }
  }

  _onCommentPress = () => {
    const { navigate } = this.props;
    if (navigate) {
      navigate(`/goals/comment/${this.state.id}`);
    }
  }



  _videoOnPress = (url: string) => {
    window.open(url, '_blank');
  }

  render() {
    const { goalId, goal, childName, videoUrl, coverPhoto, goalTag, totalAttempts, monthlyAttempts, description, notes, albums } = this.state;
    const { attemptGoalFetching, currentSettings } = this.props;

    if (!goalId) {
      return (
        <div className="goalindividual-page">
          <div className="goalindividual-container">
            <div className="page-header">
              <button className="back-button" onClick={this._onBackPress}>
                ← Back to Goals
              </button>
              <h1>Goal Not Found</h1>
            </div>
            <div className="page-content">
              <p>No goal ID provided. Please go back to the goals list.</p>
            </div>
          </div>
        </div>
      );
    }

    // Check if we have the required data
    if (!currentSettings?.child?.goals || this.state.loading) {
      return (
        <div className="goalindividual-page">
          <div className="goalindividual-container">
            <div className="loading-container">
              <div className="loading-spinner">Loading goal...</div>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="goalindividual-page">
        <div className="goalindividual-container">
          {/* Header - Matching reference project */}
          <header className="page-header">
            <div className="page-header-inner">
              <div className="header-left">
                <button className="header-button back-button" onClick={this._onBackPress}>
                  {/* <ArrowLeftIcon /> */}
                  <svg width="16" height="11" viewBox="0 0 16 11" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M5.79646 0.204319C6.07989 0.482194 6.07989 0.943465 5.80201 1.2269L2.42862 4.61141H15.2831C15.6777 4.61141 16 4.93374 16 5.33388C16 5.73402 15.6777 6.05635 15.2831 6.05635H2.42862L5.80757 9.44086C6.08545 9.72429 6.07989 10.18 5.80201 10.4634C5.51858 10.7413 5.06843 10.7413 4.78499 10.4579L0.205627 5.84517C0.144495 5.77848 0.0944773 5.70623 0.0555749 5.61731C0.0166725 5.52839 0 5.43391 0 5.33944C0 5.15048 0.0722476 4.97264 0.205627 4.8337L4.78499 0.220992C5.05731 -0.0679974 5.51303 -0.073555 5.79646 0.204319Z" fill="black"></path></svg>
                </button>
                <h1 className="header-title">
                    {childName || 'Child Name'}
                </h1>
              </div>
              <div className="header-center">
                
              </div>
              <div className="header-right">
                {/* Empty for now */}
              </div>
            </div>
          </header>

          {/* Main Content - Matching reference project */}
          <div className="page-content">
            {/* Video/Photo Section */}
            <div className="media-section">
              {/* Video Player - Implemented in Phase 4 */}
              {videoUrl ? (
                <div className="video-player">
                  <iframe 
                    src={videoUrl} 
                    title="Goal Video"
                    className="video-iframe"
                  />
                </div>
              ) : (
                /* Photo Carousel - Implemented in Phase 4 */
                <PhotoCarousel photos={coverPhoto} onImagePress={this._videoOnPress} />
              )}
            </div>

            {/* Goal Information Section */}
            <div className="goal-container">
              {/* Goal Tag Component - Implemented in Phase 3 */}
              {/* <GoalTagComponent tagType={goalTag} /> */}

              {/* Goal Description */}
              <div className="goal-description">
                <p>{description || 'Goal Description Placeholder'}</p>
              </div>

              {/* Attempts Display - Completed in Phase 5 */}
              <div className="attempts-container">
                <div className="attempt-circle">
                  <div className="circle-content">
                    <div className="attempt-number">
                      {totalAttempts}
                    </div>
                    <div className="attempt-text">
                      {this.props.t ? this.props.t("goal-individual-screen:circle.text.total-attempts") : "TOTAL ATTEMPTS"}
                    </div>
                  </div>
                </div>
                <div className="attempt-circle">
                  <div className="circle-content">
                    <div className="attempt-number">
                      {monthlyAttempts}
                    </div>
                    <div className="attempt-text">
                      {this.props.t ? this.props.t("goal-individual-screen:circle.text.monthly-attempts") : "MONTHLY ATTEMPTS"}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Notes Section */}
            <div className="notes-section">
              <h2 className="section-header">
                {this.props.t ? this.props.t("goal-individual-screen:title.notes") : "NOTES"}
              </h2>
              <div className="notes-container">
                <div className="notes-content">
                  {notes ? notes : (this.props.t ? this.props.t("goal-individual-screen:placeholder.notes") : "Click Comments to add notes")}
                </div>
              </div>
            </div>

            {/* Albums Section - Completed in Phase 6 */}
            <div className="notes-section">
              <h2 className="section-header">
                {this.props.t ? this.props.t("goal-individual-screen:title.albums") : "ALBUMS"}
              </h2>
              <div className="notes-container">
                <GalleryComponent albums={albums} goalId={goalId} previousPage="GoalIndividual" />
              </div>
            </div>
          </div>

          {/* Action Buttons - Completed in Phase 7 */}
          <div className="action-button-container">
            <button 
              className="attempt-button comman-btn" 
              onClick={this._onAttemptPress}
              disabled={attemptGoalFetching}
            >
              <span className="button-text">
                {attemptGoalFetching ? (this.props.t ? this.props.t("goal-individual-screen:loading") : "Loading...") : (this.props.t ? this.props.t("goal-individual-screen:title.button.attempt") : "ATTEMPT")}
              </span>
            </button>
            <button className="comments-button comman-btn" onClick={this._onCommentPress}>
              <span className="button-text">
                {this.props.t ? this.props.t("goal-individual-screen:title.button.comments") : "COMMENTS"}
              </span>
            </button>

          </div>
        </div>


        <AlertBox
          title={this.props.t ? this.props.t("goal-individual-screen:alert.title") : "Thanks for practising"}
          message={this.props.t ? this.props.t("goal-individual-screen:alert.message") : "Your child is one step closer to achieving this goal."}
          okAction={this._onBackPress}
          isVisible={this.state.showAlert}
          onClose={() => {}}
        />

      </div>
    );
  }

}

const mapStateToProps = (state: any) => {
  
  // const childId = state.currentSettings?.child?.id;
  // const updatedChild = state.getChildren?.children.find((child: any) => child.id === childId);
  // console.log('updatedChild', 'childId', childId, updatedChild);
  // state.currentSettings.child = updatedChild;

  return {
    currentSettings: state.currentSettings,
    attemptGoalFetching: state.attemptGoal?.fetching,
    attemptGoalSuccess: state.attemptGoal?.success,
    children: state.getChildren?.children,
  };
};

const mapDispatchToProps = (dispatch: any) => {
  // console.log('mapDispatchToProps called with dispatch:', dispatch);
  // console.log('AttemptGoalActions available:', AttemptGoalActions);
  
  return {
    attemptGoal: (id: number) => {
      console.log('Dispatching attemptGoal with ID:', id);
      console.log('AttemptGoalActions.attemptGoalRequest:', AttemptGoalActions.attemptGoalRequest);
      return dispatch(AttemptGoalActions.attemptGoalRequest(id));
    },
    getChildren: () => dispatch(GetChildrenActions.getChildrenRequest()),
    setCurrentChild: (child: any) => dispatch(CurrentSettingsActions.setCurrentChild(child)),
  };
};

export default withTranslation()(connect(mapStateToProps, mapDispatchToProps)(GoalIndividualPage));
